<template>
    <div>
        <!-- Loading State -->
        <div v-if="isLoading" class="flex items-center justify-center py-12">
            <div class="text-center">
                <svg class="animate-spin h-12 w-12 text-blue-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p class="mt-4 text-sm text-gray-600 font-medium">Loading deposits...</p>
            </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="!deposits || !deposits.data || deposits.data.length === 0" class="text-center py-12">
            <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                <i class="bx bx-receipt text-gray-400 text-3xl"></i>
            </div>
            <h3 class="text-lg font-medium text-gray-900 mb-2">No Deposits Found</h3>
            <p class="text-sm text-gray-500 mb-6">There are no deposits matching your criteria.</p>
            <button
                @click="clearFiltersAndReload"
                class="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all"
            >
                <i class="bx bx-refresh mr-2"></i>
                Reload Data
            </button>
        </div>

        <!-- Data Table -->
        <div v-else class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                    <tr>
                        <th scope="col" class="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            <div class="flex items-center space-x-1">
                                <i class="bx bx-user text-sm"></i>
                                <span>Agent</span>
                            </div>
                        </th>

                        <th scope="col" class="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            <div class="flex items-center space-x-1">
                                <i class="bx bx-money text-sm"></i>
                                <span>Amount</span>
                            </div>
                        </th>

                        <th scope="col" class="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            <div class="flex items-center space-x-1">
                                <i class="bx bx-user-check text-sm"></i>
                                <span>Deposited By</span>
                            </div>
                        </th>

                        <th scope="col" class="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            <div class="flex items-center space-x-1">
                                <i class="bx bx-calendar text-sm"></i>
                                <span>Date / Time</span>
                            </div>
                        </th>

                        <th scope="col" class="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            <div class="flex items-center space-x-1">
                                <i class="bx bx-check-shield text-sm"></i>
                                <span>Status</span>
                            </div>
                        </th>

                        <th scope="col" class="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            Actions
                        </th>
                    </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                    <tr v-for="(deposit, i) in deposits.data" :key="i" class="hover:bg-gray-50 transition-colors">
                        <td class="px-6 py-4 whitespace-nowrap">
                            <div class="flex items-center">
                               
                                <div class="ml-4">
                                    <div class="text-sm font-medium text-gray-900">
                                        {{ deposit.agent }}
                                    </div>
                                </div>
                            </div>
                        </td>

                        <td class="px-6 py-4 whitespace-nowrap">
                            <div class="text-sm font-semibold text-gray-900">
                                GMD {{ formatNumber(deposit.amount) }}
                            </div>
                        </td>

                        <td class="px-6 py-4 whitespace-nowrap">
                            <div class="text-sm text-gray-900">{{ deposit.created_by }}</div>
                        </td>

                        <td class="px-6 py-4 whitespace-nowrap">
                            <div class="text-sm text-gray-500">
                                <div>{{ formatDate(deposit.created_at) }}</div>
                            </div>
                        </td>

                        <td class="px-6 py-4 whitespace-nowrap">
                            <span v-if="deposit.status === 'deposited'"
                                class="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                <i class="bx bx-check-circle mr-1"></i>
                                Deposited
                            </span>

                            <span v-else
                                class="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                <i class="bx bx-x-circle mr-1"></i>
                                Reversed
                            </span>
                        </td>

                        <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button 
                                v-if="deposit.status === 'deposited'" 
                                @click="reverse(deposit)" 
                                class="inline-flex items-center px-3 py-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-all font-medium"
                            >
                                <i class="bx bx-revision mr-1"></i>
                                Reverse
                            </button>
                            <span v-else class="text-gray-400 text-xs">No actions</span>
                        </td>
                    </tr>
                </tbody>
            </table>

            <!-- Pagination -->
            <div class="border-t border-gray-200 bg-gray-50 px-6 py-4">
                <pagination :data="deposits" :midSize="2" @clicked="loadData" />
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import checkPermissionExist from '../../mixins/checkPermissionExist';
import { mapGetters } from 'vuex';

export default {
    props: {
        filters: {
            type: Object,
            default: () => ({}),
        },
    },

    data: () => ({
        deposits: null,
        isLoading: false,
    }),

    mixins: [checkPermissionExist],
    computed: mapGetters({
        user: 'auth/user',
    }),

    watch: {
        filters: {
            handler() {
                this.loadData(1);
            },
            deep: true,
        },
    },

    created() {
        this.loadData(1);
    },

    mounted() {
        this.emitStatistics();
    },

    methods: {
        reverse(deposit) {
            // navigator.geolocation.getCurrentPosition(setPosition);

            Swal.fire({
                title: 'Reverse Confirmation?',
                html: `Please enter the deposit of ${deposit.amount} deposited by ${deposit.created_by} for ${deposit.agent} on ${deposit.created_at}`,
                input: 'number',
                placeholder: 'Enter Amount',
                type: 'warning',
                // footer: 'fooo',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, i confirm!',
            }).then(result => {
                // console.log(result,"---REsult");
                const resultAmount = Number(result.value);
                // console.log("Amount", );
                // console.log("DAmount", deposit.amount);
                if (deposit.amount !== resultAmount) {
                    Swal.fire({
                        icon: 'warning',
                        title: 'Incorrect Amount',
                        text: 'The amount you entered does not match the deposit amount.',
                    });

                    return false;
                }
                if (result.value) {
                    axios
                        .post(
                            `/deposit/reverse?type=chapman&depositId=${deposit.id}`
                        )
                        .then(response => {
                            console.log(response);

                            // let redirect = response.data;
                            // app.$router.push({
                            //   path: `/reservation` + "/" + redirect
                            // });
                            Swal.fire({
                                icon: 'success',
                                title: 'Success!',
                                text: 'Deposit has been successfully reversed.',
                            });
                        })
                        .then(res => {
                            console.log(res);

                            setTimeout(() => {
                                // var _this = this;
                                this.loadData(1);
                            }, 1000);
                        })
                        .catch((error) => {
                            console.error('Reverse error:', error);
                            Swal.fire('Error', 'Failed to reverse deposit. Please try again.', 'error');
                        });
                }
            });
        },

        async loadData(pageNo) {
            this.isLoading = true;

            try {
                const params = new URLSearchParams();
                params.append('page', pageNo);

                if (this.filters.date_from) params.append('date_from', this.filters.date_from);
                if (this.filters.date_to) params.append('date_to', this.filters.date_to);
                if (this.filters.status) params.append('status', this.filters.status);
                if (this.filters.agent) params.append('agent', this.filters.agent);
                if (this.filters.service) params.append('service', this.filters.service);
                if (this.filters.deposited_by) params.append('deposited_by', this.filters.deposited_by);
                if (this.filters.amount_min) params.append('amount_min', this.filters.amount_min);
                if (this.filters.amount_max) params.append('amount_max', this.filters.amount_max);

                const { data } = await axios.get(`/deposits?${params.toString()}`);

                this.deposits = data;
                this.isLoading = false;

                this.emitStatistics();

                return true;
            } catch (e) {
                console.error('Error loading deposits:', e);
                this.isLoading = false;
                Swal.fire('Error', 'Failed to load deposits. Please try again.', 'error');
                return false;
            }
        },

        emitStatistics() {
            if (this.deposits && this.deposits.data) {
                const stats = {
                    total_count: this.deposits.total || this.deposits.data.length,
                    total_amount: this.deposits.data.reduce((sum, d) => sum + parseFloat(d.amount || 0), 0),
                    deposited_count: this.deposits.data.filter(d => d.status === 'deposited').length,
                    reversed_count: this.deposits.data.filter(d => d.status === 'reversed').length,
                };
                this.$emit('statistics-updated', stats);
            }
        },

        clearFiltersAndReload() {
            this.$emit('clear-filters');
            this.loadData(1);
        },

        formatNumber(num) {
            if (!num) return '0';
            return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        },

        formatDate(dateString) {
            if (!dateString) return 'N/A';
            return dateString;
        },
    },
};
</script>
